import React, { ReactElement, useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import { Formik, Form } from 'formik';
import {
  AppPrimaryButton,
  AppFormikTextField,
} from 'shared/components/AppComponents';
import CommentCard, { IComment } from './CommentCard';
import { AISystemsService } from 'services/AISystemService';
import { useParams } from 'react-router-dom';
import { Toast } from 'shared/helpers';
import { EntityType } from 'shared/enums';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

interface CommentsSectionProps {
  entityType?: string;
}

export interface CreateCommentRequest {
  entityId: string;
  entityType: string;
  content: string;
  parentCommentId: string | null;
}

const CommentsSection = ({
  entityType,
}: CommentsSectionProps): ReactElement => {
  const { aiSystemId, recordId, riskId, incidentId, vulnerabilityId } =
    useParams<{
      aiSystemId?: string;
      recordId?: string;
      riskId?: string;
      incidentId?: string;
      vulnerabilityId?: string;
    }>();

  const [comments, setComments] = useState<IComment[]>([]);
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const getEntityId = (): string | undefined => {
    switch (entityType) {
      case EntityType.AI_SYSTEM:
        return aiSystemId;
      case EntityType.DATASET:
        return recordId;
      case EntityType.RISK_REGISTER:
        return riskId;
      case EntityType.INCIDENT:
        return incidentId;
      case EntityType.VULNERABILITY:
        return vulnerabilityId;
      default:
        return undefined;
    }
  };
  const entityId = getEntityId();
  const fetchComments = (): void => {
    if (!entityId) {
      console.error('No AI System ID found');
      return;
    }
    if (!entityType) {
      console.error('No entity type found');
      return;
    }

    setIsLoading(true);

    AISystemsService.getComments(entityType, entityId)
      .then((response: any) => {
        const commentsData = response.data?.data || response.data || [];
        setComments(Array.isArray(commentsData) ? commentsData : []);
      })
      .catch((error: any) => {
        console.error('Error fetching comments:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddComment = (
    values: { comment: string },
    resetForm: () => void
  ): void => {
    if (!entityId || !entityType) return;

    const payload: CreateCommentRequest = {
      entityId,
      entityType,
      content: values.comment,
      parentCommentId: null,
    };

    AISystemsService.createComment(payload)
      .then((response: any) => {
        if (response.data?.data || response.data) {
          Toast.showSuccessMessage('Comment added successfully');
          fetchComments();
          resetForm();
        }
      })
      .catch((error: any) => {
        console.error('Error posting comment:', error);
      });
  };

  const handleToggleReply = (commentId: string): void => {
    setActiveReplyId((prev) => (prev === commentId ? null : commentId));
  };

  const handleSubmitReply = (commentId: string, text: string): void => {
    if (!entityId || !entityType) return;

    const payload: CreateCommentRequest = {
      entityId,
      entityType,
      content: text,
      parentCommentId: commentId,
    };

    AISystemsService.createComment(payload)
      .then((response: any) => {
        if (response.data?.data || response.data) {
          Toast.showSuccessMessage('Comment added successfully');
          fetchComments();
          setActiveReplyId(null);
        }
      })
      .catch((error: any) => {
        console.error('Error posting reply:', error);
      });
  };

  const handleEditComment = (commentId: string, newContent: string): void => {
    // const editCommentInTree = (comments: IComment[]): IComment[] =>
    //   comments.map((c) => {
    //     if (c._id === commentId) {
    //       return { ...c, content: newContent, isEdited: true };
    //     }
    //     if (c.replies?.length) {
    //       return { ...c, replies: editCommentInTree(c.replies) };
    //     }
    //     return c;
    //   });
    // setComments((prev) => editCommentInTree(prev));
    setIsLoading(true);
    AISystemsService.updateComment(commentId, { content: newContent })
      .then((response: any) => {
        if (response.data) {
          Toast.showSuccessMessage('Comment updated successfully');
          fetchComments();
        }
      })
      .catch((error: any) => {
        Toast.showErrorMessage(error?.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteComment = (commentId: string): void => {
    setIsLoading(true);
    AISystemsService.deleteComment(commentId)
      .then((response: any) => {
        if (response.data) {
          Toast.showSuccessMessage('Comment deleted successfully');
          fetchComments();
        }
      })
      .catch((error: any) => {
        console.error('Error deleting comment:', error);
        Toast.showErrorMessage(error?.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const totalCount = comments.length;

  const formatEntityType = (type: string): string =>
    type
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();

  useEffect(() => {
    fetchComments();
  }, [aiSystemId]);

  if (isLoading) {
    return (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper variant="outlined" className="p-4">
      <Stack direction="row" alignItems="center" spacing={1}>
        <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: 'text.primary' }} />
        <Typography variant="h5" fontWeight={700}>
          Comments ({totalCount})
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, display: 'block' }}
      >
        Discussion and notes about {formatEntityType(entityType!)}
      </Typography>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 3,
        }}
      >
        <Formik
          initialValues={{ comment: '' }}
          onSubmit={(values, { resetForm }) =>
            handleAddComment(values, resetForm)
          }
        >
          {({ handleSubmit, isSubmitting, values }) => (
            <Form onSubmit={handleSubmit}>
              <AppFormikTextField
                name="comment"
                placeholder="Write a comment..."
                multiline
                minRows={3}
              />
              <Stack direction="row" justifyContent="flex-end" mt={2}>
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.comments.postComment'
                ) && (
                  <AppPrimaryButton
                    type="submit"
                    startIcon={<SendIcon />}
                    title="Post Comment"
                    disabled={isSubmitting || !values.comment?.trim()}
                  />
                )}
              </Stack>
            </Form>
          )}
        </Formik>
      </Paper>

      <Stack spacing={3}>
        {comments.length > 0 ? (
          comments.map((comment) => (
            <CommentCard
              key={comment._id}
              comment={comment}
              onReply={handleToggleReply}
              activeReplyId={activeReplyId}
              onSubmitReply={handleSubmitReply}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
            />
          ))
        ) : (
          <Typography textAlign="center" color="text.secondary" py={4}>
            No comments yet. Be the first to comment!
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default CommentsSection;
